import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Download, QrCode, Wallet, Printer } from "lucide-react";
import { toast } from "sonner";

export default function PlanExport() {
  const [invites, setInvites] = useState([]);
  const [qrToken, setQrToken] = useState("");
  const [qrUrl, setQrUrl] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    api.get("/share/invites").then((r) => {
      const active = r.data.filter((i) => i.active);
      setInvites(active);
      if (active.length > 0) setQrToken(active[0].token);
    });
  }, []);

  useEffect(() => {
    if (!qrToken) { setQrUrl(null); return; }
    const target = `${window.location.origin}/v/${qrToken}`;
    const token = localStorage.getItem("sh_token");
    fetch(`${API_BASE}/safety-plan/qr?target=${encodeURIComponent(target)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((blob) => setQrUrl(URL.createObjectURL(blob)))
      .catch(() => setQrUrl(null));
  }, [qrToken]);

  const downloadPdf = async () => {
    const token = localStorage.getItem("sh_token");
    try {
      const res = await fetch(`${API_BASE}/safety-plan/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "safety-plan.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Could not download PDF");
    }
  };

  const printWallet = () => {
    window.print();
  };

  return (
    <div className="space-y-6 fade-up" data-testid="export-page">
      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900">Export &amp; share</h1>
        <p className="text-slate-600 mt-2 max-w-xl">Take your safety plan with you — printable, downloadable, or as a QR code on a wallet card.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="export-pdf-card">
          <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-800 flex items-center justify-center mb-3"><Download className="w-5 h-5" /></div>
          <h2 className="font-display text-lg font-bold text-slate-900">PDF safety plan</h2>
          <p className="text-sm text-slate-500 mt-1">Download a printable PDF copy of your full plan and crisis lines.</p>
          <button data-testid="export-pdf-btn" onClick={downloadPdf} className="mt-4 bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-4 py-2.5 font-semibold flex items-center gap-2">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="export-qr-card">
          <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-800 flex items-center justify-center mb-3"><QrCode className="w-5 h-5" /></div>
          <h2 className="font-display text-lg font-bold text-slate-900">QR code</h2>
          <p className="text-sm text-slate-500 mt-1">Generates a QR linked to one of your share links — perfect for ID lanyards or wallet cards.</p>
          {invites.length === 0 ? (
            <p className="mt-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="qr-no-invite">Create a share link first on the Share page.</p>
          ) : (
            <>
              <select data-testid="qr-token-select" value={qrToken} onChange={(e) => setQrToken(e.target.value)} className="mt-3 w-full border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500 text-sm">
                {invites.map((i) => (
                  <option key={i.token} value={i.token}>{`${i.name} · ${i.role}`}</option>
                ))}
              </select>
              {qrUrl && <img src={qrUrl} alt="Share QR" className="mt-4 w-44 h-44 mx-auto" data-testid="qr-image" />}
            </>
          )}
        </div>
      </div>

      {invites.length > 0 && qrUrl && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="wallet-card-area">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Wallet className="w-5 h-5 text-teal-700" /> Wallet card</h2>
            <button data-testid="wallet-print" onClick={printWallet} className="text-sm border border-slate-300 hover:bg-slate-50 rounded-xl px-3 py-1.5 flex items-center gap-1 text-slate-700">
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>

          <div ref={printRef} className="wallet-print">
            <div className="bg-gradient-to-br from-teal-700 to-teal-900 text-white rounded-2xl p-5 max-w-md mx-auto shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-teal-200">SafeHaven</div>
                  <div className="font-display text-xl font-bold mt-1">My safety plan</div>
                </div>
                <img src={qrUrl} alt="QR" className="w-20 h-20 bg-white p-1.5 rounded-lg" />
              </div>
              <div className="mt-4 text-sm text-teal-50 space-y-0.5">
                <div><strong>999</strong> · Emergency</div>
                <div><strong>NHS 111</strong> · Urgent help</div>
                <div><strong>Samaritans</strong> · 116 123</div>
                <div><strong>SHOUT</strong> · text SHOUT to 85258</div>
              </div>
              <div className="mt-3 text-[10px] text-teal-200">Scan QR to open my safety plan</div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .wallet-print, .wallet-print * { visibility: visible; }
          .wallet-print { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}
